import React, { useEffect } from 'react';
import classNames from 'classnames';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import * as validators from '../../util/validators';
import getCountryCodes from '../../translations/countryCodes';

import { FieldSelect, FieldTextInput, Heading, FieldLocationAutocompleteInput } from '../index';

import css from './AddressWithLocationForm.module.css';

/**
 * A unified address form component that combines Google location autocomplete
 * with traditional address fields. Saves both coordinates and address details.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name for the address form
 * @param {string} props.className - The class name for the address form
 * @param {string} props.locale - The locale
 * @param {intlShape} props.intl - The intl object
 * @param {boolean} props.disabled - Whether the form is disabled
 * @param {Object} props.formApi - The form API from React Final Form
 * @param {string} props.fieldId - The field ID
 * @param {Object} props.values - Current form values
 */
const AddressWithLocationForm = props => {
  const { rootClassName, className, locale, intl, disabled, formApi, fieldId, values } = props;
  const classes = classNames(rootClassName || css.root, className);

  const optionalText = intl.formatMessage({
    id: 'AddressWithLocationForm.optionalText',
  });

  // Use the language set in config.localization.locale to get the correct translations of the country names
  const countryCodes = getCountryCodes(locale);

  // Parse address from Google Maps Place data and auto-populate form fields
  useEffect(() => {
    const selectedPlace = values?.location?.selectedPlace;
    
    if (selectedPlace && formApi) {
      const { address, addressComponents } = selectedPlace;
      
      // Helper function to extract address component by type
      const getAddressComponent = (components, type, useShortName = false) => {
        if (!components) return null;
        const component = components.find(comp => comp.types.includes(type));
        return component ? (useShortName ? component.shortText : component.longText) : null;
      };
      
      // Parse address components if available (more accurate than parsing the string)
      if (addressComponents) {
        // Extract street number and route
        const streetNumber = getAddressComponent(addressComponents, 'street_number');
        const route = getAddressComponent(addressComponents, 'route');
        const premise = getAddressComponent(addressComponents, 'premise');
        const subpremise = getAddressComponent(addressComponents, 'subpremise');
        
        // Build street address
        let streetAddress = '';
        if (streetNumber && route) {
          streetAddress = `${streetNumber} ${route}`;
        } else if (route) {
          streetAddress = route;
        } else if (premise) {
          streetAddress = premise;
        }
        
        // Always update street address when new location is selected
        if (streetAddress) {
          formApi.change('recipientAddressLine1', streetAddress);
        }
        
        // Set apartment/subpremise as address line 2 (clear if not available)
        formApi.change('recipientAddressLine2', subpremise || '');
        
        // Extract city (postal_town for UK, or locality as fallback)
        const city = getAddressComponent(addressComponents, 'postal_town') || 
                     getAddressComponent(addressComponents, 'locality');
        if (city) {
          formApi.change('recipientCity', city);
        }
        
        // Extract postal code
        const postalCode = getAddressComponent(addressComponents, 'postal_code');
        if (postalCode) {
          formApi.change('recipientPostal', postalCode);
        }
        
        // Extract state/county (clear if not available)
        const state = getAddressComponent(addressComponents, 'administrative_area_level_2') ||
                      getAddressComponent(addressComponents, 'administrative_area_level_1');
        formApi.change('recipientState', state || '');
        
        // Extract country
        const country = getAddressComponent(addressComponents, 'country', true);
        if (country) {
          formApi.change('recipientCountry', country);
        }
      } else if (address) {
        // Fallback: Parse the Google address string if addressComponents not available
        const addressParts = address.split(',').map(part => part.trim());
        
        // Always update address when new location is selected
        if (addressParts.length > 0) {
          formApi.change('recipientAddressLine1', addressParts[0]);
        }
        
        if (addressParts.length > 1) {
          formApi.change('recipientCity', addressParts[1]);
        }
        
        // Try to extract postal code from the string (UK format)
        const postalCodeRegex = /([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})/i;
        const postalMatch = address.match(postalCodeRegex);
        if (postalMatch) {
          formApi.change('recipientPostal', postalMatch[0]);
        }
        
        // Set country to UK by default (as per business requirement)
        formApi.change('recipientCountry', 'GB');
      }
    }
  }, [values?.location?.selectedPlace, formApi]);

  // custom validation for postal code (UK format)
  const postalCodeValidation = message => value => {
    const VALID = undefined;
    const regex = /^([A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2})$/i;

    // undefined, null, or empty string → required
    if (typeof value === 'undefined' || value === null || !validators.isNonEmptyString(value)) {
      return message;
    }

    // non-empty string → validate format
    return regex.test(value.trim())
      ? VALID
      : 'Please enter a valid UK postal code (e.g., EC1A 1BB)';
  };

  // Helper to format location value
  const identity = v => v;

  const addressRequiredMessage = intl.formatMessage({
    id: 'AddressWithLocationForm.addressRequired',
  });
  const addressNotRecognizedMessage = intl.formatMessage({
    id: 'AddressWithLocationForm.addressNotRecognized',
  });

  return (
    <div className={classes}>
      {/* <Heading as="h3" rootClassName={css.heading}>
        <FormattedMessage id="AddressWithLocationForm.title" />
      </Heading> */}

      {/* Name Field */}
      <FieldTextInput
        id={`${fieldId}.recipientName`}
        name="recipientName"
        disabled={disabled}
        className={css.fieldFullWidth}
        type="text"
        autoComplete="name"
        label={intl.formatMessage({ id: 'AddressWithLocationForm.recipientNameLabel' })}
        placeholder={intl.formatMessage({
          id: 'AddressWithLocationForm.recipientNamePlaceholder',
        })}
        validate={validators.required(
          intl.formatMessage({ id: 'AddressWithLocationForm.recipientNameRequired' })
        )}
        // onUnmount={() => formApi?.change('recipientName', undefined)}
      />

      {/* Phone Number Field */}
      <FieldTextInput
        id={`${fieldId}.recipientPhoneNumber`}
        name="recipientPhoneNumber"
        disabled={disabled}
        className={css.fieldFullWidth}
        type="text"
        autoComplete="tel"
        label={intl.formatMessage(
          { id: 'AddressWithLocationForm.recipientPhoneNumberLabel' },
          { optionalText: optionalText }
        )}
        placeholder={intl.formatMessage({
          id: 'AddressWithLocationForm.recipientPhoneNumberPlaceholder',
        })}
        // onUnmount={() => formApi?.change('recipientPhoneNumber', undefined)}
      />

      {/* Google Location Autocomplete with Coordinates */}
      <div className={css.locationFieldWrapper}>
        <FieldLocationAutocompleteInput
          rootClassName={css.locationAddress}
          inputClassName={css.locationAutocompleteInput}
          iconClassName={css.locationAutocompleteInputIcon}
          predictionsClassName={css.predictionsRoot}
          validClassName={css.validLocation}
          name="location"
          label={intl.formatMessage({ id: 'AddressWithLocationForm.locationLabel' })}
          placeholder={intl.formatMessage({
            id: 'AddressWithLocationForm.locationPlaceholder',
          })}
          useDefaultPredictions={false}
          format={identity}
          valueFromForm={values?.location}
          /*validate={
            values?.location?.selectedPlace 
              ? undefined  // Skip validation if location already exists
              : validators.composeValidators(
                  validators.autocompleteSearchRequired(addressRequiredMessage),
                  validators.autocompletePlaceSelected(addressNotRecognizedMessage)
                )
          }*/
          disabled={disabled}
        />
      </div>

      {/* Address Line 1 & 2 */}
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.recipientAddressLine1`}
          name="recipientAddressLine1"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="address-line1"
          label={intl.formatMessage({ id: 'AddressWithLocationForm.addressLine1Label' })}
          placeholder={intl.formatMessage({
            id: 'AddressWithLocationForm.addressLine1Placeholder',
          })}
          validate={validators.required(
            intl.formatMessage({ id: 'AddressWithLocationForm.addressLine1Required' })
          )}
          // onUnmount={() => formApi?.change('recipientAddressLine1', undefined)}
        />

        <FieldTextInput
          id={`${fieldId}.recipientAddressLine2`}
          name="recipientAddressLine2"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="address-line2"
          label={intl.formatMessage(
            { id: 'AddressWithLocationForm.addressLine2Label' },
            { optionalText: optionalText }
          )}
          placeholder={intl.formatMessage({
            id: 'AddressWithLocationForm.addressLine2Placeholder',
          })}
          // onUnmount={() => formApi?.change('recipientAddressLine2', undefined)}
        />
      </div>

      {/* Postal Code & City */}
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.recipientPostalCode`}
          name="recipientPostal"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="postal-code"
          label={intl.formatMessage({ id: 'AddressWithLocationForm.postalCodeLabel' })}
          placeholder={intl.formatMessage({
            id: 'AddressWithLocationForm.postalCodePlaceholder',
          })}
          validate={postalCodeValidation(
            intl.formatMessage({ id: 'AddressWithLocationForm.postalCodeRequired' })
          )}
          // onUnmount={() => formApi?.change('recipientPostal', undefined)}
        />

        <FieldTextInput
          id={`${fieldId}.recipientCity`}
          name="recipientCity"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="address-level2"
          label={intl.formatMessage({ id: 'AddressWithLocationForm.cityLabel' })}
          placeholder={intl.formatMessage({ id: 'AddressWithLocationForm.cityPlaceholder' })}
          validate={validators.required(
            intl.formatMessage({ id: 'AddressWithLocationForm.cityRequired' })
          )}
          // onUnmount={() => formApi?.change('recipientCity', undefined)}
        />
      </div>

      {/* State & Country */}
      <div className={css.formRow}>
        <FieldTextInput
          id={`${fieldId}.recipientState`}
          name="recipientState"
          disabled={disabled}
          className={css.field}
          type="text"
          autoComplete="address-level1"
          label={intl.formatMessage(
            { id: 'AddressWithLocationForm.stateLabel' },
            { optionalText: optionalText }
          )}
          placeholder={intl.formatMessage({ id: 'AddressWithLocationForm.statePlaceholder' })}
          // onUnmount={() => formApi?.change('recipientState', undefined)}
        />

        <FieldSelect
          id={`${fieldId}.recipientCountry`}
          name="recipientCountry"
          disabled={disabled}
          className={css.field}
          label={intl.formatMessage({ id: 'AddressWithLocationForm.countryLabel' })}
          validate={validators.required(
            intl.formatMessage({ id: 'AddressWithLocationForm.countryRequired' })
          )}
        >
          <option disabled value="">
            {intl.formatMessage({ id: 'AddressWithLocationForm.countryPlaceholder' })}
          </option>
          <option key="GB" value="GB">
            United Kingdom
          </option>
          {/* Only UK for now as per business requirement */}
        </FieldSelect>
      </div>

      {/* Building/Apartment (Optional) */}
      <FieldTextInput
        id={`${fieldId}.building`}
        name="building"
        disabled={disabled}
        className={css.fieldFullWidth}
        type="text"
        label={intl.formatMessage(
          { id: 'AddressWithLocationForm.buildingLabel' },
          { optionalText: optionalText }
        )}
        placeholder={intl.formatMessage({
          id: 'AddressWithLocationForm.buildingPlaceholder',
        })}
        // onUnmount={() => formApi?.change('building', undefined)}
      />
    </div>
  );
};

export default AddressWithLocationForm;

